/**
 * Quotas d'utilisation en direct, calculés à partir de la réplique : pour
 * chaque installation (accueil, caisses, location, restaurants, remontées,
 * pistes), sa charge, sa file, son attente et son quota du moment. Les clés
 * sont celles de l'historique d'usage du serveur, pour passer de la vue en
 * direct à l'historique de la même installation.
 */
import {
  BUILDINGS,
  CLOSED,
  DIFFICULTY_LABEL,
  buildingName,
  congestionLevel,
  counterRate,
  counterService,
  liftCapacityPerHour,
  pisteComfort,
  type Building,
  type FacilityKind,
  type FacilityRef,
  type Replica,
  type UsageDay,
} from '@snowchain/shared';
import { LIFT_STATUS } from './labels';
import { duration, n0 } from './format';

export interface Quota {
  label: string;
  used: number;
  cap: number;
  unit: string;
}

export interface LiveFacility {
  key: string;
  kind: FacilityKind;
  label: string;
  ref: FacilityRef | null;
  /** Niveau de congestion en direct (CLOSED hors service). */
  level: number;
  /** Charge 0..1 et plus (débit, places, confort, parking). */
  u: number;
  /** Attente annoncée (s de simulation) et file. */
  wait: number;
  queue: number;
  /** Quota principal (ex. paires louées sur le parc, places occupées). */
  quota: Quota | null;
  /** Débit nominal, pour les comptoirs et remontées (par heure). */
  rate: number | null;
  served: number;
  lost: number;
  /** Texte court des épingles : « 2 min », « 85 % », « 312/400 ». */
  short: string;
  status: string;
  /** Position au sol de l'épingle et hauteur au-dessus du sol. */
  pos: [number, number];
  height: number;
}

/** Utilisation de la dernière tranche close (en %), si l'historique est disponible. */
function lastU(day: UsageDay | undefined, key: string): number | null {
  const u = day?.series[key]?.u;
  if (!u) return null;
  for (let i = u.length - 1; i >= 0; i--) if ((u[i] as number) >= 0) return (u[i] as number) / 100;
  return null;
}

function buildingHeight(b: Building): number {
  const [w, d] = BUILDINGS[b.kind].footprint;
  return Math.max(6, Math.min(16, Math.hypot(w, d) * 0.45));
}

/** Liste des installations mesurables, dans l'ordre de l'historique. */
export function liveFacilities(r: Replica, village: { x: number; z: number }): LiveFacility[] {
  const out: LiveFacility[] = [];
  const today = r.usage?.days[r.usage.days.length - 1];
  const s = r.station.stats;
  const all = [...r.buildings.values()];

  // Accueil et parking (commun à toute la station), épinglé sur la première zone d'accueil.
  const gate = all.find((b) => b.kind === 'welcome_area' && b.status === 'open') ?? all.find((b) => b.kind === 'chalet_accueil' && b.status === 'open');
  const pu = s.parkingCapacity ? s.onDomain / s.parkingCapacity : 0;
  const refused = s.missed ? s.missed.parking + s.missed.tickets : s.refusedToday;
  out.push({
    key: 'accueil',
    kind: 'accueil',
    label: 'Accueil et parking',
    ref: gate ? { kind: 'building', id: gate.id } : null,
    level: congestionLevel('accueil', { u: pu, x: refused > 0 && pu >= 0.97 ? 5 : 0 }),
    u: pu,
    wait: 0,
    queue: 0,
    quota: { label: 'Visiteurs présents', used: s.onDomain, cap: s.parkingCapacity, unit: 'places' },
    rate: null,
    served: s.visitorsToday,
    lost: refused,
    short: `${n0(s.onDomain)}/${n0(s.parkingCapacity)}`,
    status: pu >= 0.97 ? 'Complet' : 'Ouvert',
    pos: gate ? [gate.x, gate.z] : [village.x, village.z],
    height: gate ? buildingHeight(gate) + 4 : 10,
  });

  for (const b of all) {
    if (b.status !== 'open') continue;
    const spec = BUILDINGS[b.kind];
    const name = buildingName(b, all);
    const ref: FacilityRef = { kind: 'building', id: b.id };
    const service = counterService(b.kind);
    const multi = service !== null && !!spec.seats;
    const h = buildingHeight(b);
    if (service) {
      const key = `B${b.id}:${service}`;
      const rate = counterRate(b.kind);
      const fleet = spec.rentalsPerDay ?? 0;
      const empty = service === 'rental' && fleet > 0 && b.served >= fleet;
      // Jauge : 100 % tant que la file ne désemplit pas, sinon la charge du dernier quart d'heure.
      const u = b.queue > 0 ? 1 : (lastU(today, key) ?? 0);
      // Couleur : attente du moment ; comptoir plein sans attente = chargé.
      const level = empty ? 3 : Math.max(congestionLevel(service, { w: b.waitSec }), u >= 0.9 ? 1 : 0);
      out.push({
        key,
        kind: service,
        label: multi ? `${name} (caisses)` : name,
        ref,
        level,
        u,
        wait: b.waitSec,
        queue: b.queue,
        quota: service === 'rental' ? { label: 'Parc de skis', used: b.served, cap: fleet, unit: 'paires' } : null,
        rate,
        served: b.served,
        lost: b.lost,
        short: empty ? 'Épuisé' : b.queue > 0 ? duration(b.waitSec) : service === 'rental' ? `${n0(b.served)}/${n0(fleet)}` : 'Libre',
        status: empty ? 'Plus de skis' : b.queue > 0 ? `${n0(b.queue)} en file` : 'Sans attente',
        pos: [b.x, b.z],
        height: h,
      });
    }
    if (spec.seats) {
      const seats = spec.seats;
      const used = Math.round(b.load * seats);
      // Denrées épuisées : la salle refoule tout le monde, même vide.
      const noFood = !!r.me && r.me.stocks.food.qty < 1;
      out.push({
        key: `B${b.id}:food`,
        kind: 'food',
        label: multi ? `${name} (restaurant)` : name,
        ref,
        level: noFood ? 3 : congestionLevel('food', { u: b.load }),
        u: b.load,
        wait: 0,
        queue: 0,
        quota: { label: 'Places occupées', used, cap: seats, unit: 'places' },
        rate: null,
        served: b.meals ?? 0,
        lost: b.mealsLost ?? 0,
        short: noFood ? 'Rupture' : `${n0(b.load * 100)} %`,
        status: noFood ? 'Plus de denrées' : b.load >= 0.97 ? 'Complet' : 'Ouvert',
        pos: [b.x, b.z],
        // Le chalet porte deux épingles : celle du restaurant au-dessus de celle des caisses.
        height: multi ? h + 9 : h,
      });
    }
  }

  for (const l of r.lifts.values()) {
    if (l.status === 'construction') continue;
    const cap = liftCapacityPerHour(l.kind);
    const open = l.status === 'open';
    const u = open ? Math.min(1.2, l.throughput / cap) : 0;
    out.push({
      key: `L${l.id}`,
      kind: 'lift',
      label: l.name,
      ref: { kind: 'lift', id: l.id },
      level: open ? congestionLevel('lift', { u, w: l.waitSec }) : CLOSED,
      u,
      wait: l.waitSec,
      queue: l.queue,
      quota: { label: 'Débit', used: l.throughput, cap, unit: 'pers./h' },
      rate: cap,
      served: l.rides ?? 0,
      lost: 0,
      short: !open ? LIFT_STATUS[l.status].label : l.queue > 0 ? duration(l.waitSec) : 'Libre',
      status: LIFT_STATUS[l.status].label,
      pos: [l.bottom[0], l.bottom[1]],
      height: 12,
    });
  }

  for (const p of r.pistes.values()) {
    if (p.closedReason === 'En construction') continue;
    const comfort = pisteComfort(p);
    const u = p.traffic / comfort;
    const mid = p.path[Math.floor(p.path.length / 2)] ?? p.path[0] ?? [0, 0];
    out.push({
      key: `P${p.id}`,
      kind: 'piste',
      label: `${p.name} (${DIFFICULTY_LABEL[p.difficulty].toLowerCase()})`,
      ref: { kind: 'piste', id: p.id },
      level: p.open ? congestionLevel('piste', { u }) : CLOSED,
      u,
      wait: 0,
      queue: 0,
      quota: { label: 'Skieurs sur la piste', used: p.traffic, cap: comfort, unit: 'skieurs' },
      rate: null,
      served: 0,
      lost: 0,
      short: p.open ? `${n0(p.traffic)} sk.` : 'Fermée',
      status: p.open ? 'Ouverte' : (p.closedReason ?? 'Fermée'),
      pos: [mid[0], mid[1]],
      height: 6,
    });
  }
  return out;
}

/** Clés d'usage d'une sélection (un bâtiment peut en avoir deux : caisses et restaurant). */
export function facilityKeys(r: Replica, sel: FacilityRef): string[] {
  if (sel.kind === 'lift') return [`L${sel.id}`];
  if (sel.kind === 'piste') return [`P${sel.id}`];
  const b = r.buildings.get(sel.id);
  if (!b) return [];
  const spec = BUILDINGS[b.kind];
  const keys: string[] = [];
  const service = counterService(b.kind);
  if (service) keys.push(`B${b.id}:${service}`);
  if (spec.seats) keys.push(`B${b.id}:food`);
  if (spec.parking) keys.push('accueil');
  return keys;
}
